/**
 * Event page view model — README §1/§2 header, status band, ABOUT callout, stats and the
 * WHERE address note, per variant (copy from the `V` map in `handoff/Event Page.dc.html`).
 *
 * Pure. Everything here is computed from the EventDto, the viewer's application and what
 * the page already loaded (guest list ∩ friends). Nothing is invented: a figure the DTO
 * cannot supply renders as `—`.
 */
import { looksLikePrice } from '../../utils/eventPricing';
import React from 'react';
import { money, moneyWhole, dayLabel, dateLabel, dateYearLabel, capacityLabel, plural } from '../../utils/surfaceFormat';
import { GREEN, MAGENTA, DANGER, MUTED, AMBER_TEXT } from '../surface/tokens';
import Countdown from '../surface/Countdown';
import { categoryLabel } from '../../constants/eventCategories';
import { detectVariant, isFreeEvent, isPwywEvent, isTieredEvent, isSoldOut, isViewerHost, GOING_STATUSES } from './checkoutRailModel';

// `Countdown`'s format prop is called `style` ('short' | 'long' | 'clock'); building the
// props through a spread keeps react/style-prop-object from reading it as CSS.
const live = (to, format, extra = {}) => React.createElement(Countdown, { to, ...{ style: format }, ...extra });

export const TABS = [
  { key: 'about', label: 'ABOUT' },
  { key: 'guests', label: 'GUESTS' },
  { key: 'qa', label: 'Q + A' },
  { key: 'board', label: 'GIVE / TAKE' },
  { key: 'where', label: 'WHERE' },
];

/**
 * The tabs this viewer actually gets. A host who turned the Give / Take board off has no
 * GIVE / TAKE tab at all — not a tab that opens on "the board is off". The one thing that
 * still brings that slot back is a MIME round (the guest's COMMIT / PASS cards and the
 * commitment breakdown render there and nowhere else), and then it carries MIME's own name.
 *
 * @param {object} p
 * @param {object} p.event
 * @param {Array}  p.mimeRequests  the viewer's pending MIME asks
 * @param {Array}  p.mimeHistory   the viewer's answered MIME rounds
 */
export function eventTabs({ event, mimeRequests = [], mimeHistory = [] }) {
  const boardOn = event?.giveTakeEnabled !== false;
  const hasMime = (mimeRequests?.length || 0) > 0
    || (mimeHistory || []).some((r) => r?.status === 'Accepted');
  return TABS
    .filter((t) => t.key !== 'board' || boardOn || hasMime)
    .map((t) => (t.key === 'board' && !boardOn ? { ...t, label: 'MAKE IT MORE EPIC' } : t));
}

/** Category chips: slugs → labels, `other` → the host's custom label (never "Other"). */
export function categoryChips(event) {
  const slugs = Array.isArray(event?.categories) && event.categories.length > 0
    ? event.categories
    : (event?.category ? [event.category] : []);
  return slugs
    .map((slug) => (slug === 'other' ? (event?.customCategory || null) : categoryLabel(slug)))
    .filter(Boolean)
    .map((s) => s.toUpperCase());
}

/** Media tiles for the viewer: poster first, then the event's media (images + videos). */
export function mediaItems(event) {
  const poster = event?.posterUrl ? [{ kind: 'image', url: event.posterUrl, thumbnailUrl: event.posterUrl }] : [];
  const media = Array.isArray(event?.media) ? event.media.filter((m) => m && (m.url || m.thumbnailUrl || m.embedUrl || m.hlsUrl)) : [];
  return [...poster, ...media];
}

/** WHERE header note — address visibility by variant (README §1 WHERE). */
export function whereNote(event, variant) {
  if (!event) return '';
  if (event.status === 'ended') return 'PAST EVENT';
  if (event.locationRevealed) return 'EXACT ADDRESS SHOWN';
  if (variant === 'external') return 'SEE THE SELLER FOR DETAILS';
  if (event.mode === 'curated') return 'ADDRESS AFTER YOU ARE ACCEPTED';
  if (event.fundingType === 'party_starter') return 'ADDRESS AFTER GREENLIGHT';
  return 'EXACT ADDRESS AFTER PURCHASE';
}

function fundingPct(event) {
  if (!event) return 0;
  if (event.goalType === 'rsvp') {
    const max = Number(event.rsvpGoal || event.maxAttendees || 0);
    return max > 0 ? Math.min(100, Math.round((Number(event.acceptedCount || 0) / max) * 100)) : 0;
  }
  const goal = Number(event.fundingGoalCents || 0);
  return goal > 0 ? Math.min(100, Math.round((Number(event.totalPledgedCents || 0) / goal) * 100)) : 0;
}

/**
 * @param {object} p
 * @param {object} p.event
 * @param {object|null} p.application  viewer's application
 * @param {object} p.viewer            { isAuthenticated, profile }
 * @param {number|null} p.friendsIn    friends on the guest list, or null when the viewer cannot see the list
 * @param {number|null} p.guestCount   people on the guest list (shownCount + anonymousCount), or null
 */
export function describeEvent({ event, application = null, viewer = {}, friendsIn = null, guestCount = null }) {
  const e = event || {};
  const variant = detectVariant({ event: e, application, viewer });
  const auth = !!viewer?.isAuthenticated;
  const status = application?.status || null;
  const free = isFreeEvent(e);
  const pwyw = isPwywEvent(e);
  const tiered = isTieredEvent(e);
  const starter = e.fundingType === 'party_starter';
  const curated = e.mode === 'curated';
  const soldOut = isSoldOut(e);
  const held = GOING_STATUSES.includes(status);
  const ended = e.status === 'ended';
  const funding = e.status === 'funding';
  const host = isViewerHost(e, viewer);
  const declined = status === 'declined';
  const waitlisted = status === 'waitlisted';
  const accepted = Number(e.acceptedCount || 0);
  const max = Number(e.maxAttendees || 0);
  const qty = Number(application?.quantity || 1);
  const paidCents = application?.partyCreditsCommitted || (application?.pledgeAmountCents || 0) * qty;
  const hostedCount = e.host?.hostedEventCount ?? e.host?.eventsHostedCount ?? null;
  const escrowVerified = e.host?.escrowVerified === true;

  // ── pill + mode line ─────────────────────────────────────────────────────
  let pill; let mode;
  if (e.isExternalTicketing) { pill = { tone: 'grey', label: 'LISTED' }; mode = 'SOLD ELSEWHERE'; }
  else if (e.isHostWanted) { pill = { tone: 'yellowBordered', label: 'NEEDS A HOST' }; mode = 'UNCLAIMED · PORTAL BUILT IT'; }
  else if (starter) { pill = { tone: 'magenta', label: 'PARTY STARTER' }; mode = curated ? 'HOST REVIEWS EVERY APPLICATION' : 'OPEN · CROWDFUNDED'; }
  else if (free) { pill = { tone: 'glitchGreen', label: 'FREE' }; mode = curated ? 'HOST REVIEWS EVERY APPLICATION' : 'OPEN · NO TICKET NEEDED'; }
  else if (curated) { pill = { tone: 'lilac', label: 'CURATED' }; mode = 'HOST REVIEWS EVERY APPLICATION'; }
  else { pill = { tone: 'black', label: 'GUARANTEED' }; mode = pwyw ? 'OPEN · PAY WHAT YOU WANT' : 'OPEN · AUTO-ACCEPT'; }
  if (ended) mode = `ENDED · ${dayLabel(e.dateTime)}`;
  else if (held && !curated) mode = 'OPEN · YOU ARE IN';
  else if (soldOut && !held) mode = curated ? 'CURATED · FULL' : 'OPEN · FULL';
  else if (!auth && variant === 'guest') mode = 'OPEN · NO ACCOUNT NEEDED';

  // ── trust line ───────────────────────────────────────────────────────────
  // One denominator for a Party Starter's pledges, so the trust line and the stat never
  // disagree (they printed 2 OF 40 and 2/10 on one page): an RSVP goal counts toward the goal.
  const pledgeOf = e.goalType === 'rsvp' ? (e.rsvpGoal || max) : max;
  let trust = null;
  if (ended) trust = { text: 'THIS ONE IS DONE', color: MUTED, note: `${accepted} WENT` };
  else if (e.isHostWanted) trust = { text: 'NOBODY IS HOSTING THIS YET', color: AMBER_TEXT, note: 'CLAIM IT AND IT IS YOURS' };
  // A listing says nothing here: SOLD ELSEWHERE beside the pill and the NOT OURS callout already
  // say it. Kept as a branch so it doesn't fall through to the host's track record.
  else if (e.isExternalTicketing) trust = null;
  else if (held) trust = { text: "YOU'RE ON THE LIST", color: GREEN, note: plural(qty, 'TICKET') };
  else if (soldOut) trust = { text: 'SOLD OUT', color: DANGER, note: waitlisted ? 'YOU ARE ON THE WAITLIST' : 'WAITLIST IS OPEN' };
  else if (starter && funding) trust = { text: pledgeOf > 0 ? `${accepted} OF ${pledgeOf} PLEDGED` : plural(accepted, 'PLEDGE'), color: MAGENTA, note: free ? 'NO CHARGE' : 'HELD IN ESCROW UNTIL GREENLIGHT' };
  else if (hostedCount != null && hostedCount > 0) trust = { text: `${hostedCount} ${hostedCount === 1 ? 'EVENT' : 'EVENTS'} HOSTED`, color: GREEN, note: escrowVerified ? 'ESCROW-VERIFIED HOST' : '' };
  else if (free) trust = { text: 'FREE TO GET IN', color: '#000', note: 'HOST COVERS IT' };
  else if (hostedCount === 0) trust = { text: 'FIRST EVENT ON PORTAL', color: '#000', note: escrowVerified ? 'ESCROW-VERIFIED HOST' : '' };

  // ── status band ──────────────────────────────────────────────────────────
  let band = null;
  if (ended) band = { label: 'ENDED', value: dateYearLabel(e.dateTime), note: held ? 'RATE IT SO THE HOST GETS CREDIT' : '', bg: '#000', fg: '#fff', kind: 'ended' };
  else if (held) band = { label: "YOU'RE IN", value: `${plural(qty, 'TICKET')} · ${dayLabel(e.dateTime)}`, note: e.locationRevealed ? 'ADDRESS RELEASED' : (starter && funding ? 'ADDRESS AFTER GREENLIGHT' : ''), bg: '#000', fg: '#fff', kind: 'held' };
  else if (soldOut && !host) band = { label: 'SOLD OUT', value: `${accepted}/${max} IN`, note: waitlisted ? 'YOU ARE ON THE WAITLIST' : 'WAITLIST IS OPEN', bg: DANGER, fg: '#fff', kind: 'soldout' };
  else if (starter && funding && e.fundingDeadline) {
    const goalNote = e.goalType === 'rsvp'
      ? (e.rsvpGoal ? `HOST GREENLIGHTS AT ${e.rsvpGoal} IN` : 'HOST GREENLIGHTS WHEN IT FUNDS')
      : (e.fundingGoalCents ? `GOAL ${moneyWhole(e.fundingGoalCents)}` : 'HOST GREENLIGHTS WHEN IT FUNDS');
    band = { label: 'FUNDING', value: live(e.fundingDeadline, 'long', { suffix: ' LEFT', expiredText: 'DEADLINE PASSED' }), note: goalNote, bg: MAGENTA, fg: '#000', kind: 'funding' };
  }

  // ── ABOUT callout ────────────────────────────────────────────────────────
  let callout = null;
  if (declined) callout = { tone: 'danger', title: 'APPLICATION DECLINED', body: application?.partyCreditsRefunded ? 'The host went another way this time. Your money is back in your wallet.' : 'Your application to this event was declined by the host.' };
  else if (e.isHostWanted) callout = { tone: 'warning', title: 'THIS EVENT NEEDS A HOST', body: 'Claim it and you take over the page, the money and the guest list. We stay on the hook for the pipes.' };
  else if (e.isExternalTicketing) callout = { tone: 'grey', title: 'THIS ONE IS NOT OURS', body: 'We listed it because it is worth knowing about. Tickets, refunds and the door are all handled by the seller.' };
  else if (soldOut && !held && !host) callout = { tone: 'danger', title: 'THE ROOM IS FULL', body: 'People drop. When one does, the waitlist gets a message in order and the first to answer takes the spot.' };
  else if (status === 'payment_failed') callout = { tone: 'warning', title: 'PAYMENT FAILED', body: "Your last payment didn't go through. Your spot is still held — retry from the ticket panel." };

  // ── stats ────────────────────────────────────────────────────────────────
  const friendsStat = { n: friendsIn == null ? '—' : String(friendsIn), k: 'FRIENDS IN', color: friendsIn ? GREEN : undefined };
  let spots;
  if (ended) spots = { n: String(accepted), k: 'WENT', color: GREEN };
  else if (held) spots = { n: String(qty), k: 'YOUR TICKETS', color: GREEN };
  else if (soldOut) spots = { n: `${accepted}/${max}`, k: 'FULL', color: DANGER };
  else if (starter && funding) spots = { n: capacityLabel(accepted, pledgeOf), k: 'PLEDGED' };
  else if (e.isExternalTicketing) spots = { n: guestCount == null ? (accepted > 0 ? String(accepted) : '—') : String(guestCount), k: 'INTERESTED' };
  else if (e.fundingType === 'guaranteed' && !host) spots = max > 0 ? { n: String(max), k: 'CAPACITY' } : { n: 'OPEN', k: 'CAPACITY' }; // guaranteed hides sales velocity (host's call)
  else spots = { n: capacityLabel(accepted, max), k: max > 0 ? 'SPOTS' : 'GOING' };

  let price;
  if (e.isExternalTicketing) price = { n: (looksLikePrice(e.externalTicketDisplayPrice) ? e.externalTicketDisplayPrice.trim().toUpperCase() : '') || 'SEE SITE', k: 'PRICE' };
  else if (held && !free) price = { n: money(paidCents), k: status === 'paid' ? 'YOU PAID' : 'PLEDGED', color: GREEN };
  else if (e.isHostWanted) price = { n: moneyWhole(e.fundingGoalCents || 0), k: 'GOAL' };
  else if (starter && funding && !free) price = { n: e.goalType === 'rsvp' ? String(e.rsvpGoal || max) : moneyWhole(e.fundingGoalCents || 0), k: 'GOAL' };
  else if (free) price = { n: 'FREE', k: 'ENTRY' };
  else if (pwyw) price = { n: (e.ticketPriceCents || 0) > 0 ? `${money(e.ticketPriceCents)}+` : 'PWYW', k: 'YOU DECIDE' };
  else if (tiered || e.hasMultiplePrices) price = { n: `FROM ${money(e.displayPriceCents ?? e.ticketPriceCents ?? 0)}`, k: 'TICKET' };
  else price = { n: money(e.displayPriceCents ?? e.ticketPriceCents ?? 0), k: curated ? 'SEAT' : 'TICKET' };

  let third = friendsStat;
  if (starter && funding) third = { n: `${fundingPct(e)}%`, k: 'FUNDED', color: MAGENTA };

  let when;
  if (ended) when = { n: dateLabel(e.dateTime), k: 'HAPPENED' };
  else if (starter && funding && e.fundingDeadline) when = { n: live(e.fundingDeadline, 'short', { expiredText: 'NOW' }), k: 'TO DEADLINE' };
  else if (['selling', 'greenlit', 'funding'].includes(e.status) && e.dateTime) when = { n: live(e.dateTime, 'short', { expiredText: 'NOW' }), k: 'TO DOORS' };
  else when = { n: dateLabel(e.dateTime) || '—', k: 'DATE' };

  const stats = [spots, price, third, when];
  // Mobile 3-up: drop the third cell when it has nothing real to say.
  const mobStats = third.n === '—' ? [spots, price, when] : [spots, third, when];

  return {
    variant, pill, mode, trust, band, callout, stats, mobStats,
    free, pwyw, tiered, starter, curated, soldOut, held, ended, funding, host, declined, waitlisted,
    hostLabel: e.isHostWanted ? 'OWNED BY' : e.isExternalTicketing ? 'LISTED BY' : 'HOSTED BY',
    descNote: e.isExternalTicketing && e.sourceDomain ? `OUR WORDING · SOURCE ${String(e.sourceDomain).toUpperCase()}` : e.isExternalTicketing ? 'OUR WORDING' : 'WRITTEN BY THE HOST',
    whereNote: whereNote(e, variant),
    chips: categoryChips(e),
    // An agenda item of a club gathering carries the gathering it is part of (the header's
    // PART OF chip opens it). The gathering's own hidden pass event is not a page.
    gathering: e?.gatheringId && !e?.isGatheringPass && e?.gatheringName ? { id: e.gatheringId, name: e.gatheringName } : null,
  };
}

export default describeEvent;
